import React from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/outline";
import { useManualContext } from "../context/ManualContext";
import { ChapterItem } from "./ChapterItem";
import { useConfirmation } from "context/Confirmation";

interface ChapterListV2Props {
  collapsed: boolean;
  onToggle: () => void;
}

export const ChapterListV2: React.FC<ChapterListV2Props> = ({
  collapsed,
  onToggle,
}) => {
  const { t } = useTranslation();
  const { state, selectChapter, addChapter, deleteChapter, reorderChapters } =
    useManualContext();
  const { openConfirmation } = useConfirmation();

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // If dropped outside the list or no movement
    if (
      !destination ||
      (source.index === destination.index &&
        source.droppableId === destination.droppableId)
    ) {
      return;
    }

    const newOrder = Array.from(state.chapterOrder);
    const [movedId] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, movedId);

    reorderChapters(newOrder);
  };

  const handleAddChapter = () => {
    const chapterName = t("manual.chapter.add_chapter");
    addChapter(chapterName);
  };

  const handleDeleteChapter = (chapterId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    openConfirmation(() => deleteChapter(chapterId), {
      label: t("form.basic.label"),
      subline: t("manual.delete.confirm_subline"),
    });
  };

  if (collapsed) {
    return (
      <div className="w-10 shrink-0 bg-white rounded-lg shadow flex flex-col items-center py-4">
        <button
          onClick={onToggle}
          title={t("manual.chapter.expand")}
          aria-label={t("manual.chapter.expand")}
          className="p-1 text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 rounded-md"
        >
          <ChevronDoubleRightIcon className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-72 shrink-0 bg-white rounded-lg shadow p-4 overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-1">
          <button
            onClick={onToggle}
            title={t("manual.chapter.collapse")}
            aria-label={t("manual.chapter.collapse")}
            className="p-1 -ml-1 text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 rounded-md"
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">{t("manual.chapter.label")}</h2>
        </div>
        <button
          onClick={handleAddChapter}
          className="px-3 py-1 text-sm rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
        >
          + {t("manual.chapter.add_chapter")}
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="chapters" type="CHAPTER">
          {(provided) => (
            <ul
              className="space-y-2"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {state.chapterOrder.map((chapterId, index) => {
                const chapter = state.chapters[chapterId];
                if (!chapter) return null;

                return (
                  <ChapterItem
                    key={chapter.id}
                    chapter={chapter}
                    index={index}
                    isSelected={state.selectedChapterId === chapter.id}
                    onSelect={() => selectChapter(chapter.id)}
                    onDelete={(e) => handleDeleteChapter(chapter.id, e)}
                  />
                );
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};
